interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface CacheConfig {
  duration: number; // Cache duration in milliseconds
  cooldown: number; // Cooldown period in milliseconds
}

class ApiCache {
  private cache = new Map<string, CacheEntry<any>>();
  private lastFetchTimes = new Map<string, number>();
  private inFlight = new Map<string, Promise<any>>();
  private defaultConfig: CacheConfig = {
    duration: 5 * 60 * 1000, // 5 minutes
    cooldown: 5000, // 5 seconds
  };

  private getCacheKey(endpoint: string, params?: any): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${endpoint}${paramString}`;
  }

  private isCacheValid<T>(entry: CacheEntry<T>, config: CacheConfig): boolean {
    return Date.now() - entry.timestamp < config.duration;
  }

  private isInCooldown(endpoint: string, config: CacheConfig): boolean {
    const lastFetch = this.lastFetchTimes.get(endpoint);
    if (!lastFetch) return false;
    return Date.now() - lastFetch < config.cooldown;
  }

  async getCachedData<T>(
    endpoint: string,
    fetchFunction: () => Promise<T>,
    params?: any,
    config?: Partial<CacheConfig>
  ): Promise<T> {
    const cacheKey = this.getCacheKey(endpoint, params);
    const cacheConfig = { ...this.defaultConfig, ...config };

    // Check if we have valid cached data
    const cachedEntry = this.cache.get(cacheKey);
    if (cachedEntry && this.isCacheValid(cachedEntry, cacheConfig)) {
      return cachedEntry.data;
    }

    // If a fetch is already in-flight for this key, share that promise
    // (handles React Strict Mode double-invoke and concurrent callers)
    const existing = this.inFlight.get(cacheKey);
    if (existing) {
      return existing;
    }

    // Check cooldown — only applies when there is no in-flight request
    if (this.isInCooldown(endpoint, cacheConfig)) {
      if (cachedEntry) {
        console.warn(`API call to ${endpoint} blocked by cooldown, using cached data`);
        return cachedEntry.data;
      }
      throw new Error(`API call to ${endpoint} blocked by cooldown`);
    }

    this.lastFetchTimes.set(endpoint, Date.now());

    const fetchPromise = fetchFunction()
      .then(data => {
        this.cache.set(cacheKey, { data, timestamp: Date.now() });
        return data;
      })
      .catch(error => {
        if (cachedEntry) {
          console.warn(`API call to ${endpoint} failed, using cached data`);
          return cachedEntry.data;
        }
        // Clear cooldown so the next call can retry immediately
        this.lastFetchTimes.delete(endpoint);
        throw error;
      })
      .finally(() => {
        this.inFlight.delete(cacheKey);
      });

    this.inFlight.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  clearCache(endpoint?: string): void {
    if (endpoint) {
      for (const key of this.cache.keys()) {
        if (key.startsWith(endpoint)) {
          this.cache.delete(key);
          this.inFlight.delete(key);
        }
      }
      this.lastFetchTimes.delete(endpoint);
    } else {
      this.cache.clear();
      this.lastFetchTimes.clear();
      this.inFlight.clear();
    }
  }

  getCacheStats(): { size: number; endpoints: string[] } {
    return {
      size: this.cache.size,
      endpoints: Array.from(this.cache.keys()),
    };
  }
}

export const apiCache = new ApiCache(); 